// =====================================================================
// voApp.ts — luật thuần của vỏ app (N2): menu + đường dẫn màn, chọn nhãn khởi đầu, chữ "dữ liệu cập nhật lúc …".
// Không import React, không gọi mạng (SKILL §2) ⇒ test bằng Node.
// =====================================================================

/** 6 menu sau rà soát (§3.7) + Thông tin (QĐ-H159) theo đúng thứ tự trên thanh trên. */
export const MENU = [
  { khoa: "lop", ten: "Danh sách lớp" },
  { khoa: "kehoach", ten: "Kế hoạch mở lớp" },
  { khoa: "nghiday", ten: "Nghỉ & Dạy thay" },
  { khoa: "tacvu", ten: "Tác vụ" },
  { khoa: "nguonluc", ten: "Quản lý nguồn lực" },
  { khoa: "thongtin", ten: "Thông tin" },
  { khoa: "caidat", ten: "Cài đặt" },
] as const;

export type KhoaMenu = (typeof MENU)[number]["khoa"] | "kho-ui";
export const MAN_MAC_DINH: KhoaMenu = "lop";

export interface DuongDan {
  man: KhoaMenu;
  /** Phần sau tên màn, vd "#/caidat/ngay-le" ⇒ ["ngay-le"]. Màn tự hiểu. */
  con: string[];
}

const KHOA_HOP_LE = new Set<string>([...MENU.map((m) => m.khoa), "kho-ui"]);

/** "#/caidat/ngay-le?x=1" ⇒ {man:"caidat", con:["ngay-le"]}. Sai / trống ⇒ màn mặc định. */
export function docDuongDan(hash: string): DuongDan {
  const sach = hash.replace(/^#\/?/, "").split(/[?#]/)[0];
  const phan = sach.split("/").filter(Boolean).map((p) => {
    try { return decodeURIComponent(p); } catch { return p; }
  });
  const man = phan[0];
  if (!man || !KHOA_HOP_LE.has(man)) return { man: MAN_MAC_DINH, con: [] };
  return { man: man as KhoaMenu, con: phan.slice(1) };
}

export function taoDuongDan(d: DuongDan): string {
  return "#/" + [d.man, ...d.con].map((p) => encodeURIComponent(p)).join("/");
}

// ---------------------------------------------------------------------
// Chọn nhãn áp cho TOÀN web (QĐ-H50): bộ lọc mặc định, không chặn quyền.
// ---------------------------------------------------------------------
export const TAT_CA = "TAT_CA";
/** Giá trị nhãn đang chọn: mã nhãn, hoặc TAT_CA. */
export type ChonNhan = string;

/**
 * Nhãn khởi đầu khi mở web:
 *   1. Lựa chọn còn lưu trong TAB này (sessionStorage — tải lại trang giữ nguyên) nếu vẫn hợp lệ;
 *   2. nếu không: nhãn mặc định Admin gán cho người này (app_user.nhan_mac_dinh) nếu nhãn đó còn;
 *   3. nếu không: "Tất cả".
 */
export function chonNhanKhoiDau(daLuu: string | null, macDinh: string | null, danhSach: readonly string[]): ChonNhan {
  const coTrongDs = (m: string | null): m is string => !!m && danhSach.includes(m);
  if (daLuu === TAT_CA) return TAT_CA;
  if (coTrongDs(daLuu)) return daLuu;
  if (coTrongDs(macDinh)) return macDinh;
  return TAT_CA;
}

/** Dòng dữ liệu có nhãn `nhanDong` có hiện khi đang chọn `chon` không. Dòng không nhãn chỉ hiện ở "Tất cả". */
export function hopNhan(chon: ChonNhan, nhanDong: string | null | undefined): boolean {
  return chon === TAT_CA || nhanDong === chon;
}

// ---------------------------------------------------------------------
// Độ tươi (QĐ-H35, H59): 1 ghi chú chung trên đầu trang, theo từng nguồn.
// ---------------------------------------------------------------------
export interface NguonDoTuoi { ten: string; luc: string | null }

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Mốc ISO (UTC) ⇒ "HH:mm" nếu cùng ngày VN với `bayGio`, "HH:mm dd/mm" nếu khác ngày (giờ VN, UTC+7). */
export function chuGioVN(iso: string, bayGio: number): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "?";
  const vn = new Date(t + 7 * 3_600_000);
  const nay = new Date(bayGio + 7 * 3_600_000);
  const gio = `${pad2(vn.getUTCHours())}:${pad2(vn.getUTCMinutes())}`;
  const cungNgay = vn.getUTCFullYear() === nay.getUTCFullYear() && vn.getUTCMonth() === nay.getUTCMonth() && vn.getUTCDate() === nay.getUTCDate();
  return cungNgay ? gio : `${gio} ${pad2(vn.getUTCDate())}/${pad2(vn.getUTCMonth() + 1)}`;
}

/** cau_hinh.do_tuoi = {"nguon":[{"ten","luc"}]} (làn D ghi) ⇒ danh sách nguồn. Sai dạng ⇒ [] (ẩn ghi chú, không lỗi). */
export function docDoTuoi(giaTri: unknown): NguonDoTuoi[] {
  const nguon = (giaTri as { nguon?: unknown } | null | undefined)?.nguon;
  if (!Array.isArray(nguon)) return [];
  return nguon
    .filter((n): n is { ten: string; luc?: unknown } => !!n && typeof (n as { ten?: unknown }).ten === "string")
    .map((n) => ({ ten: n.ten, luc: typeof n.luc === "string" ? n.luc : null }));
}

/** Lớp chữ độ tươi: "File kiểm soát 14:05 · Sheet Dashboard chưa đồng bộ". Không nguồn nào ⇒ null (ẩn). */
export function chuDoTuoi(nguon: readonly NguonDoTuoi[], bayGio: number): string | null {
  if (!nguon.length) return null;
  return nguon.map((n) => `${n.ten} ${n.luc ? chuGioVN(n.luc, bayGio) : "chưa đồng bộ"}`).join(" · ");
}

/**
 * Bản gọn trên thanh trên (Le 22/10/2026: "chỉ cần cập nhật và giờ"): giờ của lần lấy MỚI NHẤT trong các nguồn, vd "14:33".
 * Chi tiết từng nguồn vẫn ở chú thích khi rê chuột (chuDoTuoi). Chưa nguồn nào đồng bộ ⇒ null (ẩn).
 */
export function gioCapNhatMoiNhat(nguon: readonly NguonDoTuoi[], bayGio: number): string | null {
  let moi: number | null = null;
  let iso: string | null = null;
  for (const n of nguon) {
    const t = n.luc ? Date.parse(n.luc) : NaN;
    if (!Number.isNaN(t) && (moi === null || t > moi)) { moi = t; iso = n.luc; }
  }
  return iso ? chuGioVN(iso, bayGio) : null;
}

/** Chữ viết tắt trên ảnh đại diện: "Lê Tuấn Long" ⇒ "LL"; email ⇒ 2 ký tự đầu. */
export function chuVietTat(hoTen: string | null, email: string): string {
  const ten = (hoTen ?? "").trim().split(/\s+/).filter(Boolean);
  if (ten.length >= 2) return (ten[0][0] + ten[ten.length - 1][0]).toUpperCase();
  if (ten.length === 1) return ten[0].slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

/**
 * Số đỏ trên menu (QĐ-H51c, H78) — bước ghép G. Tính từ gói nền đã có, không gọi server, không kéo engine vào JS ban đầu.
 * Nghỉ & Dạy thay = số ca Tiếp nhận (khối `nghi_cho`) đã qua ngày, của lớp thuộc nhãn đang chọn — CÙNG kết quả với
 * `demQuaNgay` của làn C (`core/nghiday.ts`; test voApp.test.ts khoá điều này).
 */
export function soDoMenu(
  lop: readonly { id: string; nhan: string | null }[],
  nghiCho: readonly { lop_id: string; ngay: string }[],
  chon: ChonNhan,
  homNay: string,
  /** (F, QĐ-H57) số việc trễ của Tác vụ theo nhãn đang chọn — tính ở core/tacVu.demTreTheoNhan */
  treTacVu = 0,
): Partial<Record<KhoaMenu, number>> {
  const nhanLop = new Map<string, string | null>();
  for (const l of lop) nhanLop.set(l.id, l.nhan);
  let n = 0;
  for (const c of nghiCho) if (c.ngay < homNay && hopNhan(chon, nhanLop.get(c.lop_id) ?? null)) n++;
  const out: Partial<Record<KhoaMenu, number>> = {};
  if (n) out.nghiday = n;
  if (treTacVu > 0) out.tacvu = treTacVu;
  return out;
}
